import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Badge, Button, Empty, Form, Input, Layout, Modal, Pagination, Skeleton, Tag, message } from 'antd';
import { TeamOutlined, UserOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import MainHeader from '@/components/mainheader';
import MainFooter from '@/components/mainfooter';
import { fetchConversations, createPrivateConversation, createGroupConversation } from '@/apis/chat';
import { getPostErrorMessage } from '@/apis/posts';
import { parseMemberIds, conversationPartner, conversationTitle } from '@/utils/chat';
import ChatThread from './components/ChatThread';
import ChatUserPicker from '@/components/chatuserpicker';
import NotificationsPanel from './components/NotificationsPanel';
import NotificationBadge from '@/components/notifications';
import './index.scss';
import { refreshChatSummary } from '@/store/modules/notifications';

const Message = ({ notifications = false }) => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, userInfo, sessionVersion } = useSelector(state => state.user);
  const sessionKey = `${sessionVersion}:${userInfo?.id}`;
  const [rooms, setRooms] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createType, setCreateType] = useState(null);
  const [creating, setCreating] = useState(false);
  const [retry, setRetry] = useState(0);
  const [form] = Form.useForm();
  const createLock = useRef(false);
  const redirected = useRef(false);
  const listVersion = useRef(0);
  const removeConversation = useCallback(id => {
    listVersion.current += 1;
    setRooms(current => current.filter(room => String(room.id) !== String(id)));
    setPage(1);
    setRetry(value => value + 1);
  }, []);
  useEffect(() => {
    if (isAuthenticated) {
      redirected.current = false;
      return;
    }
    if (redirected.current) return;
    redirected.current = true;
    message.warning({ key: 'message-login-required', content: '请先登录后查看消息' });
    navigate('/login', { replace: true });
  }, [isAuthenticated, navigate]);
  const onRead = useCallback(id => { setRooms(current => current.map(room => room.id === id ? { ...room, unread_count: 0, alert_unread_count: 0, has_alert_unread: false } : room)); dispatch(refreshChatSummary()).catch(() => {}); }, [dispatch]);
  useEffect(() => {
    if (!isAuthenticated || notifications) return;
    let active = true;
    let pending = false;
    const version = ++listVersion.current;
    setLoading(true); setError(''); setRooms([]); setPagination(null);
    const load = async initial => {
      if (pending || (!initial && document.hidden)) return;
      pending = true;
      try {
        const result = await fetchConversations({ page, pageSize: 20 });
        if (!Array.isArray(result?.data)) throw new Error('会话列表响应无效');
        if (active && version === listVersion.current) { setRooms(result.data); setPagination(result.pagination); setError(''); }
      } catch (err) { if (active && version === listVersion.current) setError(getPostErrorMessage(err, '会话列表加载失败')); }
      finally { pending = false; if (active && version === listVersion.current) setLoading(false); }
    };
    load(true);
    const timer = setInterval(() => load(false), 10000);
    return () => { active = false; clearInterval(timer); };
  }, [isAuthenticated, notifications, userInfo?.id, sessionVersion, page, retry]);
  const create = async values => {
    if (createLock.current) return;
    createLock.current = true; setCreating(true);
    try {
      const room = createType === 'private' ? await createPrivateConversation(values.user_id) : await createGroupConversation({ title: values.title.trim(), member_ids: parseMemberIds(values.member_ids) });
      if (!room?.id) throw new Error('会话响应无效');
      setCreateType(null); form.resetFields(); setPage(1); setRetry(value => value + 1);
      navigate(`/message/${room.id}`);
    } catch (err) { message.error(getPostErrorMessage(err, err.message || '会话创建失败')); }
    finally { createLock.current = false; setCreating(false); }
  };
  if (!isAuthenticated) return null;
  return <div className={`message ${conversationId ? 'message-detail' : ''}`}>
    <Layout>
      <MainHeader />
      <Layout.Content className="message-content">
        <nav className="message-tabs" aria-label="消息分类">
          <Link to="/message" aria-current={!notifications ? 'page' : undefined}><NotificationBadge kind="chat">聊天</NotificationBadge></Link>
          <Link to="/message/notifications" aria-current={notifications ? 'page' : undefined}><NotificationBadge kind="notifications">互动通知</NotificationBadge></Link>
        </nav>
        {notifications ? <NotificationsPanel key={sessionKey} /> : <div className="message-workspace">
          <aside className="message-sidebar"><div className="message-list-heading"><h2>我的会话</h2><div className="message-actions"><Button onClick={() => { form.resetFields(); setCreateType('private'); }}>发起私聊</Button><Button onClick={() => { form.resetFields(); setCreateType('group'); }}>创建群聊</Button></div></div>
            {error && <Alert type="error" showIcon title={error} action={<Button onClick={() => setRetry(value => value + 1)}>重试</Button>} />}
            {loading ? <Skeleton active /> : rooms.length === 0 && !error ? <Empty description="暂无会话" /> : rooms.map(room => <button className={`message-room ${String(room.id) === conversationId ? 'is-selected' : ''}`} key={room.id} onClick={() => navigate(`/message/${room.id}`)}>
              <Badge count={room.alert_unread_count ?? (room.is_muted ? 0 : room.unread_count)} size="small"><Avatar src={conversationPartner(room, userInfo?.id)?.avatar || undefined}
                alt={`${conversationTitle(room, userInfo?.id)}的头像`} icon={room.kind === 'group' ? <TeamOutlined /> : <UserOutlined />} /></Badge><div className="message-room-info"><strong>{conversationTitle(room, userInfo?.id)}</strong><span>{room.last_message?.message_type === 'image' ? '[图片]' : room.last_message?.content || '开始聊天吧'}</span></div>{room.kind === 'group' && <Tag>{room.post ? '招募群' : '群聊'}</Tag>}
              {room.is_muted && <Tag>免打扰</Tag>}
            </button>)}
            {pagination && pagination.total > 20 && <Pagination simple current={page} pageSize={20} total={pagination.total} showSizeChanger={false} onChange={setPage} />}
          </aside>
          {conversationId ? <ChatThread key={`${sessionKey}:${conversationId}`} id={conversationId} userId={userInfo?.id} onRead={onRead} onConversationRemoved={removeConversation} /> : <div className="message-welcome"><h2>和同频的搭子聊聊</h2><p>选择一个会话，或通过对方 UID 发起私聊。</p></div>}
        </div>}
      </Layout.Content>
      <MainFooter />
    </Layout>
    <Modal title={createType === 'private' ? '发起私聊' : '创建群聊'} open={Boolean(createType)} onCancel={() => { if (!creating) setCreateType(null); }} footer={null}>
      <Form form={form} layout="vertical" onFinish={create}>
        {createType === 'private' ? <Form.Item name="user_id" label="聊天对象" rules={[{ required: true, message: '请选择聊天对象' }]}><ChatUserPicker disabled={creating} /></Form.Item> : <>
          <Form.Item name="title" label="群聊名称" rules={[{ required: true, whitespace: true, message: '请输入群聊名称' }]}><Input disabled={creating} maxLength={100} /></Form.Item>
          <Form.Item name="member_ids" label="邀请成员" rules={[{ required: true, message: '请选择成员' }]}><ChatUserPicker multiple disabled={creating} /></Form.Item>
        </>}
        <Button type="primary" htmlType="submit" loading={creating} block>开始聊天</Button>
      </Form>
    </Modal>
  </div>;
};
export default Message;
